/**
 * 音频帧大小常量类
 * 
 * 集中管理前端项目中所有音频帧大小相关的常量，避免硬编码
 * 
 * @author WaveLink开发团队
 * @version 1.0
 * @since 2024.12
 */
export class AudioFrameConstants {
    
    // 私有构造函数，防止实例化
    private constructor() {
        throw new Error('常量类不能实例化');
    }
    
    // ==================== 前端音频帧大小 ====================
    
    /**
     * 后端发送给前端的音频帧大小
     * 147200字节，对应36800个立体声样本（扩大10倍以提高前端音频质量）
     */
    public static readonly FRONTEND_FRAME_SIZE: number = 14720;
    
    /**
     * 前端音频帧对应的样本数
     * 14720字节 ÷ 4字节/帧 = 3680帧
     */
    public static readonly FRONTEND_SAMPLES_PER_FRAME: number = AudioFrameConstants.FRONTEND_FRAME_SIZE / 4;
    
    /**
     * 前端目标包大小（与FRONTEND_FRAME_SIZE相同）
     */
    public static readonly FRONTEND_TARGET_PACKET_SIZE: number = AudioFrameConstants.FRONTEND_FRAME_SIZE;
    
    // ==================== 设备音频帧大小 ====================
    
    /**
     * 后端发送给设备的音频帧大小
     * 1472字节（不可修改！）
     */
    public static readonly DEVICE_FRAME_SIZE: number = 1472;
    
    /**
     * 设备音频帧对应的样本数
     * 1472字节 ÷ 4字节/帧 = 368帧
     */
    public static readonly DEVICE_SAMPLES_PER_FRAME: number = AudioFrameConstants.DEVICE_FRAME_SIZE / 4;
    
    /**
     * 设备目标包大小（与DEVICE_FRAME_SIZE相同）
     */
    public static readonly DEVICE_TARGET_PACKET_SIZE: number = AudioFrameConstants.DEVICE_FRAME_SIZE;
    
    // ==================== 通用音频帧大小 ====================
    
    /**
     * 最小音频包大小
     */
    public static readonly MIN_PACKET_SIZE: number = 1024;
    
    /**
     * 最大音频包大小
     */
    public static readonly MAX_PACKET_SIZE: number = 8192;
    
    /**
     * 标准音频帧大小（默认使用前端帧大小）
     */
    public static readonly STANDARD_FRAME_SIZE: number = AudioFrameConstants.FRONTEND_FRAME_SIZE;
    
    /**
     * 标准音频帧对应的样本数
     */
    public static readonly STANDARD_SAMPLES_PER_FRAME: number = AudioFrameConstants.FRONTEND_SAMPLES_PER_FRAME;
    
    // ==================== 缓冲区相关常量 ====================
    
    /**
     * 最大帧缓冲区大小（10帧数据）
     */
    public static readonly MAX_FRAME_BUFFER_SIZE: number = AudioFrameConstants.FRONTEND_FRAME_SIZE * 10;
    
    /**
     * 预缓冲区大小（3个音频包）
     */
    public static readonly PRE_BUFFER_SIZE: number = 3;
    
    // ==================== 时间相关常量 ====================
    
    /**
     * 帧发送间隔（毫秒）
     * 调整为100ms以匹配14720字节的音频帧大小
     * 14720字节 ÷ 4字节/帧 = 3680帧
     * 3680帧 ÷ 48000Hz = 0.0767秒 ≈ 76.7ms
     * 设置为100ms确保有足够的缓冲时间
     */
    public static readonly FRAME_SEND_INTERVAL_MS: number = 100;
    
    /**
     * 预缓冲延迟（毫秒）
     */
    public static readonly PRE_BUFFER_DELAY_MS: number = 25;
    
    /**
     * 最大实时延迟（毫秒）
     */
    public static readonly MAX_REAL_TIME_DELAY_MS: number = 50;
    
    /**
     * 预缓冲调度器间隔（毫秒）
     * 调整为100ms以匹配14720字节的音频帧大小
     * 14720字节 ÷ 4字节/帧 = 3680帧 ÷ 48000Hz = 76.7ms
     * 设置为100ms确保音频播放连续性
     */
    public static readonly PRE_BUFFER_SCHEDULER_INTERVAL_MS: number = 100;
    
    /**
     * 音频发送间隔（毫秒）
     * 用于备用发送机制的间隔
     */
    public static readonly AUDIO_SEND_INTERVAL_MS: number = 1;
    
    /**
     * 缓冲区调整间隔（毫秒）
     * 动态缓冲区大小调整的间隔
     */
    public static readonly BUFFER_ADJUSTMENT_INTERVAL_MS: number = 100;
    
    /**
     * 网络质量评估窗口大小
     * 用于计算网络质量指标的样本数量
     */
    public static readonly NETWORK_QUALITY_WINDOW_SIZE: number = 3;
    
    /**
     * 最小缓冲区大小
     * 动态缓冲区的最小包数量
     */
    public static readonly MIN_BUFFER_SIZE: number = 1;
    
    /**
     * 最大缓冲区大小
     * 动态缓冲区的最大包数量
     */
    public static readonly MAX_BUFFER_SIZE: number = 5;
    
    // ==================== 音频格式相关常量 ====================
    
    /**
     * 每个样本字节数（16位 = 2字节）
     */
    public static readonly BYTES_PER_SAMPLE: number = 2;
    
    /**
     * 每帧字节数（双声道 * 2字节 = 4字节）
     */
    public static readonly BYTES_PER_FRAME: number = 4;
    
    /**
     * 采样率
     */
    public static readonly SAMPLE_RATE: number = 48000;
    
    /**
     * 声道数
     */
    public static readonly CHANNELS: number = 2;
    
    /**
     * 位深度
     */
    public static readonly BIT_DEPTH: number = 16;
    
    // ==================== 计算相关常量 ====================
    
    /**
     * 频率分辨率（Hz/bin）
     * 48000Hz ÷ 1024 = 46.875 Hz/bin
     */
    public static readonly FREQUENCY_RESOLUTION: number = AudioFrameConstants.SAMPLE_RATE / 1024;
    
    /**
     * FFT大小
     */
    public static readonly FFT_SIZE: number = 1024;
    
    // ==================== WebSocket相关常量 ====================
    
    /**
     * WebSocket最大文本消息缓冲区大小
     */
    public static readonly WS_MAX_TEXT_MESSAGE_BUFFER_SIZE: number = 8192;
    
    /**
     * WebSocket最大二进制消息缓冲区大小
     */
    public static readonly WS_MAX_BINARY_MESSAGE_BUFFER_SIZE: number = 32768;
    
    /**
     * WebSocket会话空闲超时时间（毫秒）
     */
    public static readonly WS_MAX_SESSION_IDLE_TIMEOUT: number = 30000;
    
    /**
     * WebSocket异步发送超时时间（毫秒）
     */
    public static readonly WS_ASYNC_SEND_TIMEOUT: number = 5000;
    
    // ==================== 验证方法 ====================
    
    /**
     * 验证是否为有效的前端音频帧大小
     * 
     * @param frameSize 帧大小
     * @returns 是否为有效的前端帧大小
     */
    public static isValidFrontendFrameSize(frameSize: number): boolean {
        return frameSize === AudioFrameConstants.FRONTEND_FRAME_SIZE;
    }
    
    /**
     * 验证是否为有效的设备音频帧大小
     * 
     * @param frameSize 帧大小
     * @returns 是否为有效的设备帧大小
     */
    public static isValidDeviceFrameSize(frameSize: number): boolean {
        return frameSize === AudioFrameConstants.DEVICE_FRAME_SIZE;
    }
    
    /**
     * 验证是否为有效的音频包大小
     * 
     * @param packetSize 包大小
     * @returns 是否为有效的包大小
     */
    public static isValidPacketSize(packetSize: number): boolean {
        return packetSize >= AudioFrameConstants.MIN_PACKET_SIZE && 
               packetSize <= AudioFrameConstants.MAX_PACKET_SIZE;
    }
    
    /**
     * 根据帧大小计算样本数
     * 
     * @param frameSize 帧大小（字节）
     * @returns 样本数
     */
    public static calculateSamplesPerFrame(frameSize: number): number {
        return frameSize / AudioFrameConstants.BYTES_PER_FRAME;
    }
    
    /**
     * 根据样本数计算帧大小
     * 
     * @param samples 样本数
     * @returns 帧大小（字节）
     */
    public static calculateFrameSize(samples: number): number {
        return samples * AudioFrameConstants.BYTES_PER_FRAME;
    }
    
    /**
     * 获取音频格式信息
     * 
     * @returns 音频格式信息对象
     */
    public static getAudioFormatInfo() {
        return {
            sampleRate: AudioFrameConstants.SAMPLE_RATE,
            channels: AudioFrameConstants.CHANNELS,
            bitDepth: AudioFrameConstants.BIT_DEPTH,
            bytesPerSample: AudioFrameConstants.BYTES_PER_SAMPLE,
            bytesPerFrame: AudioFrameConstants.BYTES_PER_FRAME,
            frontendFrameSize: AudioFrameConstants.FRONTEND_FRAME_SIZE,
            deviceFrameSize: AudioFrameConstants.DEVICE_FRAME_SIZE,
            standardFrameSize: AudioFrameConstants.STANDARD_FRAME_SIZE
        };
    }
    
    /**
     * 获取WebSocket配置信息
     * 
     * @returns WebSocket配置信息对象
     */
    public static getWebSocketConfig() {
        return {
            maxTextMessageBufferSize: AudioFrameConstants.WS_MAX_TEXT_MESSAGE_BUFFER_SIZE,
            maxBinaryMessageBufferSize: AudioFrameConstants.WS_MAX_BINARY_MESSAGE_BUFFER_SIZE,
            maxSessionIdleTimeout: AudioFrameConstants.WS_MAX_SESSION_IDLE_TIMEOUT,
            asyncSendTimeout: AudioFrameConstants.WS_ASYNC_SEND_TIMEOUT
        };
    }
}

// 导出默认实例（如果需要的话）
export default AudioFrameConstants;
